import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * 비모달 플로팅 패널 컴포넌트
 *
 * 모델 작업과 동시에 팝업 작업을 할 수 있도록 드래그 가능한 플로팅 윈도우를 제공합니다.
 * - 오버레이 없음 (배경 클릭 가능)
 * - 드래그로 위치 이동 가능
 * - 리사이즈 가능 (선택적)
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - 패널 열림 상태
 * @param {Function} props.onClose - 패널 닫기 핸들러
 * @param {number|string} props.width - 패널 너비 (기본: 800)
 * @param {number|string} props.height - 패널 높이 (기본: 600)
 * @param {number} props.minWidth - 최소 너비 (기본: 400)
 * @param {number} props.minHeight - 최소 높이 (기본: 300)
 * @param {string} props.className - 추가 클래스명
 * @param {string} props.contentClassName - 컨텐츠 영역 클래스명
 * @param {boolean} props.resizable - 리사이즈 가능 여부 (기본: true)
 * @param {string} props.initialPosition - 초기 위치 ('center', 'top-right', 'bottom-right')
 * @param {React.ReactNode} props.children - 패널 내용
 */
const FloatingPanel = (props) => {
  const {
    children,
    className,
    contentClassName,
    width = 800,
    height = 600,
    minWidth = 400,
    minHeight = 300,
    isOpen,
    onClose,
    resizable = true,
    initialPosition = 'center',
    ...rest
  } = props;

  // 패널 위치 상태
  const [position, setPosition] = useState({ x: 0, y: 0 });
  // 패널 크기 상태
  const [size, setSize] = useState({
    width: typeof width === 'number' ? width : parseInt(width) || 800,
    height: typeof height === 'number' ? height : parseInt(height) || 600,
  });
  // 드래그 상태
  const [isDragging, setIsDragging] = useState(false);
  // 리사이즈 상태
  const [isResizing, setIsResizing] = useState(false);
  // 초기화 여부
  const [isInitialized, setIsInitialized] = useState(false);

  const panelRef = useRef(null);
  const dragStartRef = useRef({ x: 0, y: 0 });
  const resizeStartRef = useRef({ width: 0, height: 0, x: 0, y: 0 });

  // 크기 계산 (화면 크기 제한 적용)
  const calculateSize = useCallback((w, h) => {
    const maxW = window.innerWidth - 40;
    const maxH = window.innerHeight - 40;
    return {
      width: Math.min(typeof w === 'number' ? w : parseInt(w) || 800, maxW),
      height: Math.min(typeof h === 'number' ? h : parseInt(h) || 600, maxH),
    };
  }, []);

  // 위치 계산 (크기에 맞춰 중앙 정렬)
  const calculatePosition = useCallback((panelSize) => {
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;

    switch (initialPosition) {
      case 'top-right':
        return {
          x: Math.max(0, windowWidth - panelSize.width - 20),
          y: 20,
        };
      case 'bottom-right':
        return {
          x: Math.max(0, windowWidth - panelSize.width - 20),
          y: Math.max(0, windowHeight - panelSize.height - 20),
        };
      case 'center':
      default:
        return {
          x: Math.max(0, (windowWidth - panelSize.width) / 2),
          y: Math.max(0, (windowHeight - panelSize.height) / 2),
        };
    }
  }, [initialPosition]);

  // 패널이 열릴 때 초기 위치 설정
  useEffect(() => {
    if (isOpen && !isInitialized) {
      const newSize = calculateSize(width, height);
      const newPos = calculatePosition(newSize);
      setSize(newSize);
      setPosition(newPos);
      setIsInitialized(true);
    } else if (!isOpen) {
      setIsInitialized(false);
    }
  }, [isOpen, isInitialized, calculateSize, calculatePosition, width, height]);

  // width, height prop 변경 시 size 및 position 업데이트
  useEffect(() => {
    if (isOpen && isInitialized) {
      const newSize = calculateSize(width, height);
      const newPos = calculatePosition(newSize);
      setSize(newSize);
      setPosition(newPos);
    }
  }, [width, height, isOpen, isInitialized, calculateSize, calculatePosition]);

  // 드래그 시작
  const handleDragStart = useCallback((e) => {
    // 버튼이나 인터랙티브 요소에서는 드래그하지 않음
    if (e.target.closest('button, input, select, textarea, [data-no-drag]')) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
    dragStartRef.current = {
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    };
  }, [position]);

  // 드래그 중
  const handleDragMove = useCallback((e) => {
    if (!isDragging) return;

    const newX = e.clientX - dragStartRef.current.x;
    const newY = e.clientY - dragStartRef.current.y;

    // 화면 밖으로 나가지 않도록 제한
    const maxX = window.innerWidth - 100; // 최소 100px는 보이도록
    const maxY = window.innerHeight - 50;

    setPosition({
      x: Math.max(-size.width + 100, Math.min(newX, maxX)),
      y: Math.max(0, Math.min(newY, maxY)),
    });
  }, [isDragging, size.width]);

  // 드래그 종료
  const handleDragEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  // 리사이즈 시작
  const handleResizeStart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    resizeStartRef.current = {
      width: size.width,
      height: size.height,
      x: e.clientX,
      y: e.clientY,
    };
  };

  // 리사이즈 중
  const handleResizeMove = useCallback((e) => {
    if (!isResizing) return;

    const deltaX = e.clientX - resizeStartRef.current.x;
    const deltaY = e.clientY - resizeStartRef.current.y;

    const newWidth = Math.max(minWidth, resizeStartRef.current.width + deltaX);
    const newHeight = Math.max(minHeight, resizeStartRef.current.height + deltaY);

    // 화면을 벗어나지 않도록 제한
    const maxWidth = window.innerWidth - position.x - 20;
    const maxHeight = window.innerHeight - position.y - 20;

    setSize({
      width: Math.min(newWidth, maxWidth),
      height: Math.min(newHeight, maxHeight),
    });
  }, [isResizing, minWidth, minHeight, position.x, position.y]);

  // 리사이즈 종료
  const handleResizeEnd = useCallback(() => {
    setIsResizing(false);
  }, []);

  // 드래그/리사이즈 이벤트 리스너
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleDragMove);
      window.addEventListener('mouseup', handleDragEnd);
      return () => {
        window.removeEventListener('mousemove', handleDragMove);
        window.removeEventListener('mouseup', handleDragEnd);
      };
    }
  }, [isDragging, handleDragMove, handleDragEnd]);

  useEffect(() => {
    if (isResizing) {
      window.addEventListener('mousemove', handleResizeMove);
      window.addEventListener('mouseup', handleResizeEnd);
      return () => {
        window.removeEventListener('mousemove', handleResizeMove);
        window.removeEventListener('mouseup', handleResizeEnd);
      };
    }
  }, [isResizing, handleResizeMove, handleResizeEnd]);

  const panelClass = classNames(
    'floating-panel',
    className
  );

  const contentClass = classNames(
    'floating-panel-content',
    'rounded-lg shadow-2xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700',
    contentClassName
  );

  const panelContent = (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          ref={panelRef}
          className={panelClass}
          style={{
            position: 'fixed',
            left: position.x,
            top: position.y,
            width: size.width,
            height: size.height,
            zIndex: 40, // Dialog overlay(z-30)보다 높게
            cursor: isDragging ? 'grabbing' : 'default',
          }}
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.15 }}
          {...rest}
        >
          <div className={contentClass} style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', position: 'relative', overflow: 'hidden' }}>
            {/* 컨텐츠 */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
              {children}
            </div>

            {/* 드래그 핸들 영역 - 헤더 위에 투명 레이어로 배치 */}
            <div
              className="floating-panel-drag-handle"
              onMouseDown={handleDragStart}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '56px', // 헤더 높이만큼
                cursor: isDragging ? 'grabbing' : 'grab',
                zIndex: 5,
                // 버튼 영역 제외 (우측 250px는 버튼 영역)
                clipPath: 'inset(0 250px 0 0)',
              }}
            />

            {/* 리사이즈 핸들 (우하단) */}
            {resizable && (
              <div
                className="floating-panel-resize-handle"
                onMouseDown={handleResizeStart}
                style={{
                  position: 'absolute',
                  right: 0,
                  bottom: 0,
                  width: 20,
                  height: 20,
                  cursor: 'nwse-resize',
                  background: 'transparent',
                  zIndex: 10,
                }}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  style={{ position: 'absolute', right: 4, bottom: 4 }}
                >
                  <path
                    d="M14 14L14 8M14 14L8 14M10 14L14 10"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    className="text-gray-400"
                  />
                </svg>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  // Portal을 사용하여 body에 직접 렌더링
  return createPortal(panelContent, document.body);
};

FloatingPanel.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  minWidth: PropTypes.number,
  minHeight: PropTypes.number,
  className: PropTypes.string,
  contentClassName: PropTypes.string,
  resizable: PropTypes.bool,
  initialPosition: PropTypes.oneOf(['center', 'top-right', 'bottom-right']),
};

FloatingPanel.defaultProps = {
  width: 800,
  height: 600,
  minWidth: 400,
  minHeight: 300,
  resizable: true,
  initialPosition: 'center',
};

export default FloatingPanel;
